import React, { useState, useContext, forwardRef } from 'react';
import { useHistory } from 'react-router';
import { useQuery } from '@apollo/react-hooks';
import { Pagination } from '@mui/material';
import Rating from '@mui/material/Rating';

import './desktop-product-detail-review-style.scss';

import { Loading } from '@atoms';
import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import ProductListTitle from '@src/componentsNew/ProductListTitle/ProductListTitle';
import DesktopReviewListItem from '@src/componentsNew/ListItem/ReviewListItem/DesktopReviewListItem';
import ReviewWriteForm from '../Form/ReviewWriteForm/ReviewWriteForm';

import StarIcon from '@src/componentsNew/Icons/Icons/StarIcon';
import PendingIcon from '@src/componentsNew/Icons/Icons/PendingIcon';
import SortListIcon from '@src/componentsNew/Icons/Icons/SortListIcon';
import FilterListIcon from '@src/componentsNew/Icons/Icons/FilterListIcon';
import InfoIcon from '@src/componentsNew/Icons/Icons/InfoIcon';

import { OrderDirection, ReviewSortField } from '__generated__/globalTypes';
import {
  product_product_variants,
  product_product_productReviewsByUser,
} from '@components/ProductPage/ProductDetailPage/__generated__/product';
import { REVIEWS } from '@molecules/ReviewList/queries';
import { reviews, reviewsVariables } from '@molecules/ReviewList/__generated__/reviews';

import { CredentialContext, credentialType } from '@providers';
import { REVIEWS_SORTING_FILTER_OPTIONS, REVIEWS_FILTER_OPTIONS } from '@src/utils/constants';
import { getGraphqlIdFromDBId } from '@src/core/utils';
import { redirectToLoginWithCurrentPath } from '@src/utils/authCheck';

export interface IProps {
  productId: string;
  productRefetch?: any;
  variants: (product_product_variants | null)[];
  showVariants?: boolean;
  recentReview?: product_product_productReviewsByUser | null;
  productTotalReviewCount?: number;
  isUserBlockedProductReview?: boolean;
}
const reviewsToShow = 7;

const DesktopProductDetailReview = forwardRef<HTMLDivElement, IProps>(
  (
    {
      productId,
      productRefetch,
      variants,
      showVariants,
      recentReview,
      productTotalReviewCount,
      isUserBlockedProductReview,
    },
    ref,
  ) => {
    const history = useHistory();
    const { currentUser } = useContext<credentialType>(CredentialContext);

    const initialVariables: reviewsVariables = {
      product: productId,
      first: reviewsToShow,
      sortBy: {
        field: ReviewSortField.CREATE_AT,
        direction: OrderDirection.DESC,
      },
    };

    const [variables, setVariables] = useState<reviewsVariables>(initialVariables);

    // Sorting Option
    const [selectedSortingOption, setSelectedSortingOption] = useState(
      REVIEWS_SORTING_FILTER_OPTIONS.find(
        (option) =>
          option.value.field === variables.sortBy.field && option.value.direction === variables.sortBy.direction,
      ),
    );

    // Filter Option
    const [selectedFilteringOptionItems, setSelectedFilteringOptionItems] = useState({
      hasImage: variables?.hasImage ? true : null,
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);

    const [isReviewExist, setIsReviewExist] = useState(false);
    const [isOpenReviewWriteForm, setIsOpenReviewWriteForm] = useState(false);

    const { data: reviewsData, loading, refetch } = useQuery<reviews>(REVIEWS, {
      variables,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const reviews = data.reviews;

        setIsReviewExist(reviews.totalCount > 0);
      },
    });

    const handleClickWriteReviewButton = () => {
      if (currentUser?.token) {
        setIsOpenReviewWriteForm(true);
      } else {
        redirectToLoginWithCurrentPath(history);
      }
    };

    const handleClickSortingOptionButton = (option) => {
      setSelectedSortingOption(option);

      setCurrentPage(1);
      setVariables((prev) => {
        const updatedVariables = { ...prev };

        if ('after' in updatedVariables) {
          delete updatedVariables['after'];
        }
        updatedVariables['sortBy'] = option.value;

        return updatedVariables;
      });
    };

    const handleClickFilteringOptionItem = (optionName, value) => {
      if (value !== selectedFilteringOptionItems.hasImage) {
        setSelectedFilteringOptionItems((prevItems) => ({
          ...prevItems,
          [optionName]: value,
        }));

        setCurrentPage(1);
        setVariables((prev) => {
          const updatedVariables = { ...prev };

          if ('after' in updatedVariables) {
            delete updatedVariables['after'];
          }
          updatedVariables[optionName] = value;

          return updatedVariables;
        });
      }
    };

    const goTotalReviewSection = () => {
      const reveiwArea = document.getElementById('desktop-product-detail-review-area');
      if (reveiwArea) {
        const elementPosition = reveiwArea.getBoundingClientRect().top;
        const offsetPosition = elementPosition - 65;

        window.scrollBy({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    };

    const handleClickPageButton = (pageNumber: number) => {
      goTotalReviewSection();
      const lastReviewIndex = reviewsToShow * (pageNumber - 1) - 1;
      const cursor = getGraphqlIdFromDBId(lastReviewIndex.toString(), 'arrayconnection');

      setCurrentPage(pageNumber);
      setVariables((prev) => ({
        ...prev,
        after: cursor,
      }));
    };

    if (loading) {
      return <Loading />;
    }

    return (
      <div id="desktop-product-detail-review-area" ref={ref}>
        <ProductListTitle
          title="Reviews & Ratings"
          typoVariant="h8"
          typoComponent="h6"
          typoColor="#212121"
          isShowLine={true}
        />

        {productTotalReviewCount > 0 ? (
          <div className="count-sorting-wrapper">
            <div className="review-count-wrapper">
              <Rating max={5} value={5} icon={<StarIcon fillColor="#212121" />} readOnly />

              <Typography cName="variant-text" variant="body3" component="span" style={{ color: '#666666' }}>
                {`${reviewsData?.reviews?.totalCount} Reviews`}
              </Typography>
            </div>

            <div className="review-sorting-filter-wrapper">
              <div className="sorting-wrapper">
                <SortListIcon />
                {REVIEWS_SORTING_FILTER_OPTIONS.map((sortingItem, idx) => (
                  <Button
                    key={idx}
                    cName={`review-sorting-btn ${selectedSortingOption.label === sortingItem.label ? 'selected' : ''}`}
                    size="small"
                    onClick={() => handleClickSortingOptionButton(sortingItem)}
                  >
                    {sortingItem.label}
                  </Button>
                ))}
              </div>
              <span>|</span>
              <div className="filtering-wrapper">
                <FilterListIcon />
                {REVIEWS_FILTER_OPTIONS.map((parentOption) =>
                  parentOption.options.map((childOption, idx) => (
                    <Button
                      key={idx}
                      cName={`review-sorting-btn ${
                        selectedFilteringOptionItems.hasImage === childOption.value ? 'selected' : ''
                      }`}
                      size="small"
                      onClick={() => handleClickFilteringOptionItem(parentOption.name, childOption.value)}
                    >
                      {childOption.title}
                    </Button>
                  )),
                )}
              </div>
            </div>
          </div>
        ) : null}

        {!isUserBlockedProductReview ? (
          <>
            {(!recentReview || recentReview?.status === 'REJECTED') &&
              (isOpenReviewWriteForm ? (
                <div className="review-form-wrapper">
                  <ReviewWriteForm
                    productId={productId}
                    productRefetch={productRefetch}
                    variants={variants}
                    showVariants={showVariants}
                  />
                </div>
              ) : (
                <div className="write-review-btn-wrapper">
                  <Button cName="write-review-btn" size="small" onClick={handleClickWriteReviewButton}>
                    Write Review
                  </Button>

                  <div className="review-reward-point-text-wrapper">
                    <InfoIcon />

                    <Typography variant="caption" component="p" style={{ color: '#666666' }}>
                      Currently, points not being awarded for writing reviews
                    </Typography>
                  </div>
                </div>
              ))}

            {recentReview?.status === 'PENDING' && (
              <div className="pending-text-wrapper">
                <PendingIcon />
                <Typography cName="pending-text" variant="subtitle2" component="span" style={{ color: '#4CAF50' }}>
                  Your review is currently under review
                </Typography>
              </div>
            )}

            {!isReviewExist && (
              <div className="no-review-text-wrapper">
                <Typography variant="body1" component="p" style={{ color: '#9E9E9E' }}>
                  No Reviews Yet
                </Typography>
              </div>
            )}
          </>
        ) : !isReviewExist ? (
          <div className="no-review-text-wrapper">
            <Typography variant="body1" component="p" style={{ color: '#9E9E9E' }}>
              No Reviews Yet
            </Typography>
          </div>
        ) : null}

        {isReviewExist && (
          <>
            <ul className="product-detail-review-item-wrapper">
              {reviewsData?.reviews?.edges.map((edge) => (
                <DesktopReviewListItem key={edge.node.id} review={edge.node} isPreview={false} refetch={refetch} />
              ))}
            </ul>
            <Pagination
              className="review-pagination"
              size="large"
              count={Math.ceil(reviewsData.reviews.totalCount / reviewsToShow)}
              page={currentPage}
              onChange={(_, page) => handleClickPageButton(page)}
            />
          </>
        )}
      </div>
    );
  },
);

export default DesktopProductDetailReview;
